import { createContext, useContext, useState, useMemo, useEffect } from "react";
import { FCX } from "@datassential/platform-ui-lib";
import { AffinityAmong, Metric } from "@core/models/Metric";

export enum RawNorm {
	Raw,
	Norm,
}

export type ProfileContextType = {
	rawNorms: RawNorm;
	setRawNorms: (value: RawNorm) => void;
	categoryId: number | null | undefined;
	setCategoryId: (value: number | null | undefined) => void;
	amongThose: AffinityAmong;
	setAmongThose: (value: AffinityAmong) => void;
	trendMetric: Metric;
	setTrendMetric: (value: Metric) => void;
};

const defaultContext: ProfileContextType = {
	rawNorms: RawNorm.Raw,
	setRawNorms: () => null,
	categoryId: null,
	setCategoryId: () => null,
	amongThose: AffinityAmong.All,
	setAmongThose: () => null,
	trendMetric: Metric.KnowIt,
	setTrendMetric: () => null,
};

const ProfileContext = createContext<ProfileContextType>(defaultContext);

export const useProfileContext = () => {
	const context = useContext(ProfileContext);
	if (!context) throw new Error("Profile context is not initialized");
	return context;
};

const ProfileContextProvider: FCX = ({ children }) => {
	const [rawNorms, setRawNorms] = useState<ProfileContextType['rawNorms']>(defaultContext.rawNorms);
	const [categoryId, setCategoryId] = useState<ProfileContextType['categoryId']>(defaultContext.categoryId);
	const [amongThose, setAmongThose] = useState<ProfileContextType['amongThose']>(defaultContext.amongThose);
	const [trendMetric, setTrendMetric] = useState<ProfileContextType['trendMetric']>(defaultContext.trendMetric);

	const value = useMemo(() => ({
		rawNorms,
		setRawNorms,
		categoryId,
		setCategoryId,
		amongThose,
		setAmongThose,
		trendMetric,
		setTrendMetric,
	}), [rawNorms, categoryId, amongThose, trendMetric]);

	useEffect(() => {
		if (rawNorms === RawNorm.Raw) {
			setCategoryId(null);
		}
	}, [rawNorms])

	return (
		<ProfileContext.Provider value={value}>
			{children}
		</ProfileContext.Provider>
	);
};

export default ProfileContextProvider; 